import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {ContextualMenuMediaControlOptions} from '@Components/contextual-menu/components/contextual-menu-media-control-options';
import type {MediaControlsItemProps} from '@Components/contextual-menu/components/media-controls-item';
import {openCropperModal} from '@Modals/cropper-modal/cropper-modal.library';
import type {CropperPanelApplyResponse} from '@Panels/cropper-panel';
import {getCurrentPage} from '@Components/poster-editor/poster-editor-reducer';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.class';
import type {ReplaceMediaControlsButtons} from '@Components/replace-media-controls';
import {onOpenReplaceBackground} from '@Components/poster-editor/library/poster-editor-open-modals';
import {ContextualMenuReplaceMediaControls} from '@Components/contextual-menu/components/contextual-menu-replace-media-controls';
import {ImageBackground} from '@PosterWhiteboard/page/background/image-background.class';
import {useAppSelector} from '@/hooks';
import styles from './background-image-media-controls.module.scss';

export function BackgroundImageMediaControls(): ReactElement<any> {
  const currentPageObject = useAppSelector(getCurrentPage);
  const [isReplaceOptionEnabled, setIsReplaceOptionEnabled] = useState(false);

  if (currentPageObject?.background.details.type !== BackgroundTypeName.IMAGE) {
    throw new Error(`Expected background type to be of image`);
  }

  const getItems = (): Array<MediaControlsItemProps> => {
    return [
      {
        id: 'background-image-crop',
        className: styles.cropOption,
        icon: 'icon-crop',
        text: window.i18next.t('pmwjs_crop'),
        onClick: onOpenCrop,
      },
      {
        id: 'background-image-replace',
        icon: 'icon-replace',
        text: window.i18next.t('pmwjs_replace'),
        onClick: onToggleReplaceOption,
        className: styles.cropOption,
      },
    ];
  };

  const onCropCallback = (response: CropperPanelApplyResponse): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      return;
    }
    void currentPage.background.updateFromObject({
      details: {
        imageBackgroundItemObject: {
          cropData: {
            cropped: true,
            ...response.cropData,
          },
        },
      },
    });
  };

  const onOpenCrop = (): void => {
    const poster = window.posterEditor?.whiteboard?.pages.poster;
    if (!poster) {
      return;
    }

    const backgroundItem = poster.getCurrentPage().background.details;
    if (!(backgroundItem instanceof ImageBackground)) {
      return;
    }

    openCropperModal({
      onApply: onCropCallback,
      imageHashedFilename: backgroundItem.imageBackgroundItem.hashedFilename,
      imageSource: backgroundItem.imageBackgroundItem.imageSource,
      dataUrl: backgroundItem.imageBackgroundItem.uploadingImageData?.dataUrl,
      showRemoveBackgroundOption: false,
      initialCropData: backgroundItem.imageBackgroundItem.cropData,
      showCancelButton: false,
      showCroppingPresets: false,
      aspectRatio: poster.width / poster.height,
    });
  };

  const onReplaceCallback = (id: ReplaceMediaControlsButtons): void => {
    onOpenReplaceBackground(id);
  };

  const onToggleReplaceOption = (): void => {
    setIsReplaceOptionEnabled(true);
  };

  return isReplaceOptionEnabled ? (
    <ContextualMenuReplaceMediaControls
      id="background-image-replace-media-controls"
      controlsClassName={styles.replaceOption}
      onClick={onReplaceCallback}
      onCancel={(): void => {
        setIsReplaceOptionEnabled(false);
      }}
    />
  ) : (
    <ContextualMenuMediaControlOptions id="background-image-media-controls" items={getItems()} />
  );
}
